import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Save } from '../../icons';
import {
  Button, DatePicker, EmptyState, FormField, Input, PageHeader, Select, Table,
  type TableColumn,
  StatTile, StatTiles
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  getOvertimeSettlement, listWageProfiles, saveWageProfile, formatWon,
  WAGE_TYPES, WAGE_TYPE_LABEL, DEFAULT_MONTHLY_STANDARD_MINUTES,
  type SettlementDto, type WageProfileDto, type WageType,
} from '../../api/hr-wage';
import { formatWorkMinutes } from '../../lib/daily-work';
import { ApproverPicker, type PickedUser } from '../ApprovalPage/ApproverPicker';
import './AttendanceAdmin.css';

/**
 * 화면 Q — **연장 정산**. 근태 6단계(E2), 2026-08-24.
 *
 * 설계: `docs/attendance-round2-sample.html` 제안 7 · 화면 Q.
 *
 * ★★ **근태는 시간까지, 급여는 금액부터.** 연장·야간·휴일이 **몇 시간**인지는 근태가 정하고,
 *    그것이 **얼마**인지는 임금제와 통상임금을 아는 급여가 정한다. 그래서 이 화면의 금액은
 *    전부 **예상**이고, 확정은 급여 명세를 만들 때 정해진다.
 * ★★ 금액이 비어 있는 줄은 **0 원이 아니라 「임금 정보 없음」** 이다 — 0 으로 적으면
 *    "수당이 없다" 로 읽히는데 그것은 사실이 아니라 우리가 모른다는 뜻이다. 합계도 그 사람 수를
 *    함께 적는다(빼고 더한 합계만 보이면 합계가 거짓말이 된다).
 * ★ **포괄임금제는 계산이 아니라 판정**이다. 약정 시간 안이면 0 원이고, 약정을 모르면
 *   판정 자체를 하지 않는다.
 * ★ 임금 프로필은 **시행일자 판**이다 — 임금이 바뀌면 새 행을 넣는다(고치면 지난달이 흔들린다).
 * ★★ 2026-08-24 육안 확인에서 걸린 것 — **연장이 있는 사람만 줄이 서므로**, 처음에는 아무도
 *    임금 정보를 등록할 수 없었다(줄이 있어야 폼이 열렸다). 순서가 거꾸로다 — 임금은 연장보다
 *    **먼저** 있는 사실이다. 그래서 「임금 정보」 절을 따로 두고 **사람을 골라** 등록한다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 기준 셋) ─────────────────────────
 * `PageHeader` · `Select` ×3 · `DatePicker` · `FormField` · `Input` · `Table` ×2 ·
 * `Button` · `EmptyState`.
 *
 * ★★ **「근태는 시간까지, 급여는 금액부터」 배경 설명은 도움말로 갔다** — 정산을 백 번 열어도
 *    백 번 같은 말이다(정본 P8).
 * ★★ 반대로 **밑줄의 「기준」은 화면에 남겼다** — 야간 창·이 달 휴일 수·「주말은 아직 세지
 *    않는다」는 **지금 이 표가 무엇을 센 것인가**이고, 달마다 값이 바뀐다.
 * ★★ **임금제에 따라 달라지는 설명도 화면에 남겼다** — 고른 값에 따라 문장이 바뀌므로
 *    「늘 같은 말」이 아니다(포괄임금은 계산이 아니라 판정 · 시급제 · 통상임금 나눗수).
 */

const ERRORS: Record<string, string> = {
  FORBIDDEN: '연장 정산은 회사 관리자만 볼 수 있습니다.',
  USER_NOT_FOUND: '이 회사에 없는 사용자입니다.',
  BASE_AMOUNT_REQUIRED: '월 통상임금(연봉제는 연봉)을 입력하세요.',
  HOURLY_RATE_REQUIRED: '시급제는 약정 시급이 필요합니다.',
  INVALID_DATE: '적용 시작일 형식이 잘못되었습니다.',
};

const REASON_NOTE: Record<string, string> = {
  NO_PROFILE: '임금 정보 없음',
  NO_BASE: '통상임금 미등록',
  NO_FIXED_HOURS: '약정 시간 미등록',
  WITHIN_FIXED: '약정 안 — 가산 없음',
};

function errText(err: unknown, fallback: string): string {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
  return ERRORS[code] ?? fallback;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Draft {
  userId: string;
  name: string;
  wageType: WageType;
  amount: string;
  fixedOtHours: string;
  standardHours: string;
  effectiveFrom: string;
}

export function WagePage(): JSX.Element {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState<SettlementDto | null>(null);
  const [profiles, setProfiles] = useState<WageProfileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  /** 사람 고르기 — 연장이 없는 사람도 등록할 수 있어야 한다(기존 ApproverPicker 재사용). */
  const [picked, setPicked] = useState<PickedUser[]>([]);

  const load = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const [s, p] = await Promise.all([getOvertimeSettlement(y, m), listWageProfiles().catch(() => [])]);
      setData(s);
      setProfiles(p);
    } catch (err) {
      setData(null);
      setError(errText(err, '연장 정산을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(year, month); }, [load, year, month]);

  const profileByUser = useMemo(() => {
    const m = new Map<string, WageProfileDto>();
    for (const p of profiles) m.set(p.userId, p);
    return m;
  }, [profiles]);

  function openDraft(row: { userId: string; name: string }) {
    const p = profileByUser.get(row.userId);
    const type = (p?.wageType as WageType) ?? 'MONTHLY';
    setDone(null);
    setError(null);
    setDraft({
      userId: row.userId,
      name: row.name,
      wageType: WAGE_TYPES.includes(type) ? type : 'MONTHLY',
      amount: String(p?.wageType === 'HOURLY' ? (p?.hourlyRate ?? '') : (p?.baseAmount ?? '')),
      fixedOtHours: p?.fixedOtMinutes != null ? String(p.fixedOtMinutes / 60) : '',
      standardHours: String((p?.monthlyStandardMinutes ?? DEFAULT_MONTHLY_STANDARD_MINUTES) / 60),
      effectiveFrom: p?.effectiveFrom ?? `${year}-${String(month).padStart(2, '0')}-01`,
    });
  }

  async function onSave() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const amount = Number(draft.amount.replace(/[,\s]/g, ''));
      const standardHours = Number(draft.standardHours);
      const fixedHours = draft.fixedOtHours.trim() === '' ? null : Number(draft.fixedOtHours);
      await saveWageProfile(draft.userId, {
        wageType: draft.wageType,
        baseAmount: draft.wageType === 'HOURLY' ? null : (Number.isFinite(amount) ? amount : null),
        hourlyRate: draft.wageType === 'HOURLY' ? (Number.isFinite(amount) ? amount : null) : null,
        // ★ 빈 칸은 0 이 아니라 **모른다**로 보낸다 — 0 으로 보내면 약정 안의 근로까지 수당이 붙는다.
        fixedOtMinutes: draft.wageType === 'INCLUSIVE' && fixedHours != null ? Math.round(fixedHours * 60) : null,
        monthlyStandardMinutes: Number.isFinite(standardHours) && standardHours > 0
          ? Math.round(standardHours * 60) : DEFAULT_MONTHLY_STANDARD_MINUTES,
        effectiveFrom: draft.effectiveFrom || todayIso(),
      });
      setDone(`${draft.name} 님의 임금 정보를 저장했습니다 — 이 달 정산부터 반영됩니다.`);
      setDraft(null);
      await load(year, month);
    } catch (err) {
      setError(errText(err, '저장하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  const total = data?.total;

  /**
   * 이 달에 **휴가로 갈음한 사람이 있나** (2026-09-06 · 판 B).
   * ★ 옛 서버는 이 칸을 안 준다 → 0 → 열이 안 선다(지금까지와 한 자도 안 달라진다).
   */
  const hasSubstituted = (data?.total.substitutedPeople ?? 0) > 0;

  const cols: ReadonlyArray<TableColumn<SettlementDto['rows'][number]>> = useMemo(() => [
    {
      key: 'name',
      header: '이름',
      width: '170px',
      rowHeader: true,
      render: (r) => (
        <>
          {r.name}
          {r.employeeNumber ? <span className="hra__dim"> · {r.employeeNumber}</span> : null}
        </>
      ),
    },
    {
      key: 'wageType',
      header: '임금제',
      width: '140px',
      // 칸마다 하는 말이 다르다 — 여기는 **누르면 등록**, 금액 칸은 **모른다**.
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openDraft(r)}
          aria-label={`${r.name} 임금 정보 ${r.wageTypeLabel ? '수정' : '등록'}`}
        >
          {r.wageTypeLabel ?? '임금제 등록'}
        </Button>
      ),
    },
    { key: 'ot', header: '연장', width: '90px', render: (r) => formatWorkMinutes(r.premium.overtimeMinutes) },
    {
      key: 'night',
      header: '야간',
      width: '90px',
      render: (r) => (
        <span className={r.premium.nightMinutes > 0 ? undefined : 'is-muted'}>
          {r.premium.nightMinutes > 0 ? formatWorkMinutes(r.premium.nightMinutes) : '—'}
        </span>
      ),
    },
    {
      key: 'holiday',
      header: '휴일',
      width: '90px',
      render: (r) => {
        const holiday = r.premium.holidayMinutes + r.premium.holidayOverMinutes;
        return (
          <span className={holiday > 0 ? undefined : 'is-muted'}>
            {holiday > 0 ? formatWorkMinutes(holiday) : '—'}
          </span>
        );
      },
    },
    /**
     * 휴가로 **갈음된** 몫 (2026-09-06 · 판 B).
     *
     * ★★★ **갈음이 하나도 없으면 이 열이 아예 안 선다.** 정책이 꺼진 회사(지금 전부)에서
     *    늘 「—」인 열을 세우면 담당자는 「이게 뭐지」를 매번 묻는다.
     * ★★ 왼쪽 셋(연장·야간·휴일)은 **일한 사실**이라 갈음과 무관하게 그대로다. 금액만
     *    줄어드는데 이 열이 없으면 **왜 줄었는지 화면 어디에도 없다.**
     */
    ...(hasSubstituted ? [{
      key: 'substituted',
      header: '휴가로 갈음',
      width: '120px',
      render: (r: SettlementDto['rows'][number]) => {
        const took = r.substituted;
        const sum = took ? took.overtimeMinutes + took.holidayMinutes + took.holidayOverMinutes : 0;
        return (
          <span className={sum > 0 ? 'is-warn' : 'is-muted'}>
            {sum > 0 ? `− ${formatWorkMinutes(sum)}` : '—'}
          </span>
        );
      },
    } as TableColumn<SettlementDto['rows'][number]>] : []),
    { key: 'mult', header: '가산 배수', render: (r) => <span className="is-muted">{r.estimate.multiplierNote}</span> },
    {
      key: 'amount',
      header: '수당(예상)',
      width: '150px',
      align: 'right',
      render: (r) => {
        const note = REASON_NOTE[r.estimate.reason];
        return (
          <span className={r.estimate.amount === null ? 'is-warn' : 'is-strong'}>
            {r.estimate.amount === null ? (note ?? '계산 불가') : formatWon(r.estimate.amount)}
            {r.estimate.amount !== null && r.estimate.reason === 'WITHIN_FIXED' && (
              <span className="hra__dim"> · {REASON_NOTE.WITHIN_FIXED}</span>
            )}
          </span>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [profileByUser, year, month, hasSubstituted]);

  const profileCols: ReadonlyArray<TableColumn<WageProfileDto>> = useMemo(() => [
    { key: 'who', header: '사람', render: (p) => data?.rows.find((r) => r.userId === p.userId)?.name ?? `사용자 ${p.userId}` },
    { key: 'type', header: '임금제', width: '120px', render: (p) => p.wageTypeLabel },
    { key: 'hourly', header: '통상임금 시급', width: '140px', align: 'right', render: (p) => (p.hourlyWage === null ? '—' : formatWon(p.hourlyWage)) },
    { key: 'from', header: '적용 시작', width: '124px', render: (p) => <span className="is-muted">{p.effectiveFrom}</span> },
    {
      key: 'edit',
      header: '',
      width: '96px',
      render: (p) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => openDraft({ userId: p.userId, name: data?.rows.find((r) => r.userId === p.userId)?.name ?? `사용자 ${p.userId}` })}
        >
          고치기
        </Button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [data, profileByUser]);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/wages')}
        scope={scopeFor('/erp/attendance/wages')}
        title="연장 정산"
        description="승인된 연장만 셉니다 · 금액은 «예상»입니다."
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>근태는 시간까지만 확정합니다.</b> 연장 · 야간 · 휴일이 <b>몇 시간</b>인지는 근태가
                정하고, 그것이 <b>얼마</b>인지는 임금제와 통상임금을 아는 급여가 정합니다 —
                두 곳에서 각자 계산하면 같은 달에 두 숫자가 생깁니다. 그래서 이 화면의 금액은
                <b> 예상</b>이고, 확정은 급여 명세를 만들 때입니다.</p>
              <p>금액이 비어 있는 줄은 <b>0 원이 아니라 「임금 정보 없음」</b>입니다 — 0 으로 적으면
                「수당이 없다」로 읽히는데, 사실은 <b>우리가 모른다</b>는 뜻입니다. 합계도 그 사람 수를
                함께 적습니다.</p>
              <p>임금 프로필은 <b>시행일자 판</b>입니다. 임금이 바뀌면 <b>적용 시작일을 새로 잡아 다시
                저장</b>하세요 — 같은 날짜로 저장하면 그 행을 고치고, 지난 행을 고치면
                <b> 지난달 정산이 소급해서 바뀝니다</b>.</p>
            </>
          ),
        }}
        actions={(
          <>
            <Select
              ariaLabel="연도" size="sm" value={String(year)} onChange={(v) => setYear(Number(v))}
              options={[now.getUTCFullYear() - 1, now.getUTCFullYear(), now.getUTCFullYear() + 1]
                .map((y) => ({ value: String(y), label: `${y}년` }))}
            />
            <Select
              ariaLabel="월" size="sm" value={String(month)} onChange={(v) => setMonth(Number(v))}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}월` }))}
            />
          </>
        )}
      />

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok"><Check size={14} aria-hidden />{done}</p>}
      {loading && <p className="hra__empty">불러오는 중…</p>}

      {!loading && data && data.rows.length === 0 && (
        <p className="hra__empty">
          이 달에 <b>승인된 연장</b>이 없습니다 — 신청·승인된 연장만 셉니다.
        </p>
      )}

      {!loading && data && data.rows.length > 0 && (
        <>
          {/* 정본 D9-2 요약 타일 (2026-09-06 신설) — 손짜기 `hra__stat` 에서 옮겨 왔다. */}
          <StatTiles ariaLabel="이 달 요약">
            <StatTile label="평일 연장 ×1.5" value={formatWorkMinutes(total?.premium.overtimeMinutes ?? 0)} />
            <StatTile label="야간 +0.5 (겹침)" value={formatWorkMinutes(total?.premium.nightMinutes ?? 0)} />
            <StatTile
              label="휴일 (8시간 초과 ×2.0)"
              value={formatWorkMinutes((total?.premium.holidayMinutes ?? 0) + (total?.premium.holidayOverMinutes ?? 0))}
            />
            {/*
              ★ 「N명 제외」는 합계를 읽는 데 꼭 필요한 단서라 곁말로 붙인다 —
              빼고 더한 합계만 보이면 **합계가 거짓말이 된다**.
            */}
            <StatTile
              label="예상 합계"
              value={formatWon(total?.amount ?? 0)}
              note={total && total.unknownWage > 0 ? `${total.unknownWage}명 제외` : undefined}
              tone={total && total.unknownWage > 0 ? 'warning' : 'default'}
            />
          </StatTiles>

          {/* ★ 「무엇을 센 것인가」는 값에 따라 달라지므로 화면에 남는다(정본 P8). */}
          <p className="hra__note">
            {data.year}년 {data.month}월 · 승인된 연장만 · <b>{total?.people ?? 0}명</b>.
            야간은 연장·휴일과 <b>겹칩니다</b> — 시간을 더하면 없는 시간이 생깁니다.
            {total && total.unknownWage > 0 && (
              <> 임금 정보가 없는 <b>{total.unknownWage}명</b>은 합계에서 빠져 있습니다.</>
            )}
            {/*
              ★★★ **금액이 왜 줄었는지를 화면이 말한다** (2026-09-06 · 판 B).
                 §57 보상휴가는 「임금 지급을 «갈음»하여」이므로, 휴가로 돌린 시간은 수당에서
                 빠진다 — 안 적으면 「시간은 그대로인데 금액만 줄었다」로 읽힌다.
            */}
            {hasSubstituted && total?.substituted && (
              <> <b>{total.substitutedPeople}명</b>이 보상휴가(§57)로 돌린{' '}
                <b>{formatWorkMinutes(
                  total.substituted.overtimeMinutes + total.substituted.holidayMinutes
                  + total.substituted.holidayOverMinutes,
                )}</b>
                은 <b>수당에서 빠져 있습니다</b> — 휴가로 주고 수당도 주면 이중입니다.
              </>
            )}
          </p>

          <Table
            caption={`${data.year}년 ${data.month}월 연장 정산`}
            className="hra-tbl"
            columns={cols}
            rows={data.rows}
            rowKey={(r) => r.userId}
          />
        </>
      )}

      {/* 임금 정보 — **연장과 무관하게** 등록한다. 임금은 연장보다 먼저 있는 사실이다. */}
      <section className="hra__rule" aria-label="임금 정보 등록">
        <h2 className="hra__rulehead">
          임금 정보 <span className="hra__dim">{profiles.length}명 등록됨</span>
        </h2>
        {profiles.length === 0 ? (
          <p className="hra__note">
            아직 아무도 등록되지 않았습니다 — 등록해야 위 표의 <b>수당(예상)</b>이 채워집니다.
          </p>
        ) : (
          <Table
            caption="등록된 임금 정보"
            className="hra-tbl"
            columns={profileCols}
            rows={profiles}
            rowKey={(p) => p.id}
            empty={<EmptyState title="등록된 임금 정보가 없습니다." />}
          />
        )}
        <div className="hra__form">
          <ApproverPicker
            label="사람 고르기"
            hint="이름으로 찾아 고르면 그 사람의 임금 정보를 등록합니다 — 연장이 없어도 됩니다."
            value={picked}
            onChange={(next) => {
              const one = next[next.length - 1];
              setPicked([]);
              if (one) openDraft({ userId: one.userId, name: one.name });
            }}
          />
        </div>
      </section>

      {draft && (
        <section className="hra__aside" aria-label="임금 정보">
          <h2 className="hra__rulehead">{draft.name} 님의 임금 정보</h2>
          <div className="hra__form">
            <FormField label="임금제">
              <Select
                ariaLabel="임금제"
                value={draft.wageType}
                onChange={(v) => setDraft({ ...draft, wageType: v as WageType })}
                options={WAGE_TYPES.map((t) => ({ value: t, label: WAGE_TYPE_LABEL[t] }))}
              />
            </FormField>
            <FormField label={draft.wageType === 'HOURLY' ? '약정 시급(원)' : draft.wageType === 'ANNUAL' ? '연봉(원)' : '월 통상임금(원)'}>
              <Input
                inputMode="numeric"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder={draft.wageType === 'HOURLY' ? '12000' : '3000000'}
              />
            </FormField>
            {draft.wageType === 'INCLUSIVE' && (
              <FormField label="약정 연장시간(시간/월)">
                <Input
                  inputMode="decimal"
                  value={draft.fixedOtHours}
                  onChange={(e) => setDraft({ ...draft, fixedOtHours: e.target.value })}
                  placeholder="20"
                />
              </FormField>
            )}
            {draft.wageType !== 'HOURLY' && (
              <FormField label="월 소정근로(시간)">
                <Input
                  inputMode="decimal"
                  value={draft.standardHours}
                  onChange={(e) => setDraft({ ...draft, standardHours: e.target.value })}
                  placeholder="209"
                />
              </FormField>
            )}
            <FormField label="적용 시작일">
              <DatePicker
                value={draft.effectiveFrom || null}
                onChange={(v) => setDraft({ ...draft, effectiveFrom: v ?? '' })}
              />
            </FormField>
          </div>
          {/* ★ 고른 임금제에 따라 문장이 바뀐다 — 「늘 같은 말」이 아니라 지금의 판정이다. */}
          <p className="hra__note">
            {draft.wageType === 'INCLUSIVE' ? (
              <><b>포괄임금제는 계산이 아니라 판정입니다.</b> 약정 시간 안이면 가산이 0 이고 넘긴 분만 가산합니다.
                약정 시간을 비워 두면 <b>판정하지 않습니다</b> — 0 시간으로 보지 않습니다.
                야간 가산(+0.5)은 약정과 별개로 더합니다.</>
            ) : draft.wageType === 'HOURLY' ? (
              <>시급제는 <b>약정 시급</b>이 곧 통상임금 시급입니다.</>
            ) : (
              <>통상임금 시급 = {draft.wageType === 'ANNUAL' ? '연봉 ÷ 12 ' : ''}월 통상임금 ÷ 월 소정근로시간.
                <b> 209시간은 주 40시간 기준</b>이라 소정이 다르면 나눗수도 달라집니다.</>
            )}
          </p>
          <div className="hra__actions">
            <Button variant="secondary" onClick={() => setDraft(null)} disabled={busy}>취소</Button>
            <Button iconLeft={<Save size={14} aria-hidden />} onClick={() => { void onSave(); }} loading={busy}>
              저장
            </Button>
          </div>
        </section>
      )}

      {/* ★ 「무엇을 기준으로 셌나」는 달마다 값이 바뀐다 — 화면에 남는다(정본 P8). */}
      {data?.basis && (
        <p className="hra__note">
          야간은 {data.basis.nightWindow} 입니다. 휴일은 <b>회사 일정에 있는 날</b>만 셉니다(이 달 {data.basis.holidayDates}일)
          {data.basis.weeklyRestCounted ? '' : ' — 주말(주휴일)은 아직 세지 않습니다'}.
          이 화면은 <b>급여 명세를 만들지 않습니다</b> — 확정 금액은 급여에서 정해집니다.
        </p>
      )}
    </div>
  );
}
